import React, {useState} from "react";
import {useFormContext} from "react-hook-form";
import type {PersonModelInterface} from "@/models/personModel";

import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Separator} from "@/components/ui/separator";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Button} from "@/components/ui/button";

import {
    Smartphone,
    Hash,
    Cloud,
    KeyRound,
    ShieldCheck,
    FileBadge,
    Eye,
    EyeOff,
} from "lucide-react";

function FieldShell({
                        icon: Icon,
                        label,
                        children,
                        error,
                        right,
                    }: {
    icon: any;
    label: string;
    children: React.ReactNode;
    error?: string;
    right?: React.ReactNode;
}) {
    return (
        <div className="flex items-start gap-3 py-2">
            <div className="mt-0.5 rounded-md border bg-muted/40 p-2">
                <Icon className="h-4 w-4 text-muted-foreground"/>
            </div>

            <div className="min-w-0 flex-1 space-y-2">
                <Label className="text-xs text-muted-foreground">{label}</Label>
                <div className="flex items-start gap-2">
                    <div className="flex-1">{children}</div>
                    {right ? <div className="pt-0.5">{right}</div> : null}
                </div>
                {error ? <p className="text-xs text-destructive">{error}</p> : null}
            </div>
        </div>
    );
}

const PhoneInfoForms: React.FC<{ className?: string }> = ({className}) => {
    const {
        register,
        formState: {errors},
    } = useFormContext<PersonModelInterface>();

    const [showPassword, setShowPassword] = useState(false);

    return (
        <Card className={className}>
            <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Smartphone className="h-4 w-4"/>
                    Информация о телефоне
                </CardTitle>
            </CardHeader>

            <CardContent className="space-y-3">
                {/* Модель как отдельный блок, как в просмотре */}
                <div className="rounded-lg border bg-muted/20 p-3">
                    <div className="text-xs text-muted-foreground mb-2">Модель</div>

                    <div className="space-y-2">
                        <Label>Модель телефона</Label>
                        <Input
                            {...register("phone.phoneModel", {required: "Обязательное поле"})}
                        />
                        {errors.phone?.phoneModel?.message ? (
                            <p className="text-xs text-destructive">
                                {errors.phone.phoneModel.message}
                            </p>
                        ) : null}
                    </div>
                </div>

                <div className="grid gap-1 md:grid-cols-2">
                    <FieldShell
                        icon={Hash}
                        label="IMEI"
                        error={errors.phone?.imei?.message}
                    >
                        <Input
                            {...register("phone.imei", {
                                required: "Обязательное поле",
                                pattern: {
                                    value: /^\d{15,}$/,
                                    message: "IMEI должен содержать минимум 15 цифр",
                                },
                                validate: (value: string) => {
                                    if (/^0+$/.test(String(value).trim())) return "IMEI не может быть 0";
                                    return true;
                                },
                            })}
                        />
                    </FieldShell>

                    <FieldShell
                        icon={Cloud}
                        label="iCloud"
                        error={errors.phone?.icloud?.message as any}
                    >
                        <Input
                            {...register("phone.icloud", {
                                pattern: {
                                    value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
                                    message: "Неверный формат email",
                                },
                            })}
                        />
                    </FieldShell>

                    <FieldShell
                        icon={KeyRound}
                        label="Пароль"
                        error={errors.phone?.password?.message}
                        right={
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => setShowPassword((v) => !v)}
                                className="h-9 w-9"
                            >
                                {showPassword ? (
                                    <EyeOff className="h-4 w-4"/>
                                ) : (
                                    <Eye className="h-4 w-4"/>
                                )}
                            </Button>
                        }
                    >
                        <Input
                            type={showPassword ? "text" : "password"}
                            {...register("phone.password", {required: "Обязательное поле"})}
                        />
                    </FieldShell>

                    <FieldShell
                        icon={ShieldCheck}
                        label="Состояние телефона"
                        error={errors.phone?.condition?.message}
                    >
                        <Input
                            {...register("phone.condition", {required: "Обязательное поле"})}
                        />
                    </FieldShell>

                    <FieldShell
                        icon={FileBadge}
                        label="Номер регистрации"
                        error={errors.phone?.registrationNumber?.message}
                    >
                        <Input
                            {...register("phone.registrationNumber", {
                                required: "Обязательное поле",
                            })}
                        />
                    </FieldShell>
                </div>

                <Separator/>

                <p className="text-xs text-muted-foreground">
                    В режиме редактирования пароль вводится и может быть показан/скрыт.
                </p>
            </CardContent>
        </Card>
    );
};

export default PhoneInfoForms;
